import { useState } from 'react'

import {
  Box,
  Item,
  Spinner,
  Title,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { A3InnuvaRolesStepDocument } from 'generated/resources/graphql'
import { z } from 'zod'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { jobPositionsToOptions } from 'modules/payroll_integrations_a3innuva/lib/a3innuvaHelpers'
import {
  A3InnunvaSource,
  A3InnuvaMapping,
  A3InnuvaWizardData,
  a3InnuvaSourceSchema,
} from 'modules/payroll_integrations_a3innuva/lib/types'

import { DefaultValue } from './DefaultValue'
import { FactorialRolesSection } from './FactorialRolesSection'
import { RolesRadiobuttons } from './RolesRadiobuttons'

const a3InnuvaJobPositionSchema = z.object({
  mappings: z
    .array(
      z.object({
        factorial_id: z.string(),
        a3innuva_id: z.string(),
        meta: z
          .object({
            uuid: z.string().optional(),
            legal_entity_id: z.string().optional(),
          })
          .optional(),
      })
    )
    .optional(),
})

const getCompanyCodes = (answers: { [key: string]: string }): string | null => {
  if (answers.company_code_enabled === 'true') {
    return answers.company ? answers.company : null
  }
  return null
}

const getLegalEntityCodes = (answers: { [key: string]: string }): string | null => {
  if (answers.company_code_enabled === 'true') {
    return null
  }

  const legalEntityMatcher = /^legal_entity_\d+$/
  const legalEntityKeys = Object.keys(answers).filter((key) => legalEntityMatcher.test(key))
  const legalEntityCodes: { [key: number]: string } = {}
  legalEntityKeys.forEach((key) => {
    if (answers[key] !== undefined) {
      legalEntityCodes[Number(key.slice(13))] = answers[key]
    }
  })

  return JSON.stringify(legalEntityCodes)
}

const RolesStep = ({ data, onDataChanged }: WizardStepRenderParams<A3InnuvaWizardData>) => {
  const session = useCurrentSession()
  const jobPositions: A3InnunvaSource | undefined = data.sources?.job_positions || { mappings: [] }

  const [roleSectionType, setRoleSectionType] = useState<
    'factorial_roles' | 'default_job_position'
  >('factorial_roles')

  useUnsafeEffect(() => {
    if (data.sources?.job_positions === undefined) {
      const parsedData = a3InnuvaJobPositionSchema.safeParse(data.answers.job_positions)
      onDataChanged({
        ...data,
        sources: {
          ...data.sources,
          job_positions: parsedData.success ? parsedData.data : { mappings: [] },
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const legalEntityIds = session.company.legalEntities.map((entity) => entity.id)
  const mainLegalEntityId = session.company.legalEntities.find((entity) => entity.main)?.id

  const { data: rolesData, loading } = useQuery(A3InnuvaRolesStepDocument, {
    variables: {
      companyId: session.company.id,
      currentUserId: session.access.userId,
      legalEntityIds: legalEntityIds,
      companyCode: getCompanyCodes(data.answers),
      legalEntityCodes: getLegalEntityCodes(data.answers),
    },
  })

  const a3innuvaJobPositions = rolesData?.payrollIntegrationsA3innuva.jobPositions ?? []

  const a3innuvaJobPositionsHash = a3innuvaJobPositions.reduce(
    (acc: Record<number, (typeof a3innuvaJobPositions)[number][]>, job) => {
      if (typeof job.legalEntityId !== 'number') {
        return acc
      }

      if (!acc[job.legalEntityId]) {
        acc[job.legalEntityId] = []
      }

      acc[job.legalEntityId]?.push(job)
      return acc
    },
    {}
  )
  const legalEntities = rolesData?.jobCatalog.roles.flatMap((node) => node.legalEntities) ?? []
  const uniqueLegalEntities = [...new Set(legalEntities)]
  const options = (legalEntityId: number): Item[] => {
    return a3innuvaJobPositionsHash[legalEntityId]
      ? jobPositionsToOptions(a3innuvaJobPositionsHash[legalEntityId])
      : []
  }
  const companyLevels =
    rolesData?.jobCatalog.roles.flatMap((node) => {
      return node.levels.map((level) => {
        return level
      })
    }) ?? []

  const companyRoles = rolesData?.jobCatalog.roles ?? []

  const legalEntityLevelsMap = new Map()

  companyRoles.forEach((role) => {
    role.legalEntities.forEach((entity) => {
      if (!legalEntityLevelsMap.has(entity.id)) {
        legalEntityLevelsMap.set(entity.id, [])
      }
      legalEntityLevelsMap
        .get(entity.id)
        .push(...companyLevels.filter((level) => level.role?.id === role.id))
    })
  })

  const groupedLevelsByLegalEntity = Array.from(legalEntityLevelsMap.entries()).map(
    ([entityId, levels]) => ({
      entityId,
      levels: levels.sort((a: { roleName: string }, b: { roleName: string }) =>
        a.roleName.localeCompare(b.roleName)
      ),
    })
  )

  useOnNext(() => {
    trackEvent()
    onDataChanged({
      ...data,
      answers: {
        ...data.answers,
        job_positions: JSON.parse(JSON.stringify(jobPositions)),
      },
    })
  })

  const trackEvent = () => {
    track(trackingEvents.payrollIntegrationsA3Innuva.a3innuvatSettingsJobPositionsSelected, {
      company_id: session.company.id,
      access_id: session.access.id,
      job_positions: jobPositions?.mappings?.map((jobPosition) => jobPosition.a3innuva_id),
    })
  }

  const updateMappings = (newMappings: A3InnuvaMapping[]) => {
    onDataChanged({
      ...data,
      sources: {
        ...data.sources,
        job_positions: {
          mappings: newMappings,
        },
      },
    })
  }

  return loading ? (
    <Spinner />
  ) : (
    <Box gap="s24" width="s600">
      <Title
        title={i18n.t(
          'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.levels.wizard_title'
        )}
        description={i18n.t(
          'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.levels.description'
        )}
      />

      <Box justifyContent="flexStart" flexDirection="column" gap="s32">
        <RolesRadiobuttons
          roleSectionType={roleSectionType}
          onRoleSectionTypeChange={setRoleSectionType}
        />

        <Box paddingBottom="s40">
          {roleSectionType === 'factorial_roles' && (
            <FactorialRolesSection
              uniqueLegalEntities={uniqueLegalEntities}
              groupedLevelsByLegalEntity={groupedLevelsByLegalEntity}
              jobPositions={jobPositions}
              options={options}
              updateMappings={updateMappings}
              loading={loading}
            />
          )}
          {roleSectionType === 'default_job_position' && (
            <DefaultValue
              mappings={jobPositions.mappings || []}
              options={options(mainLegalEntityId ?? 0)}
              onMappingChanged={updateMappings}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}

export const rolesStep = (): WizardStep<A3InnuvaWizardData> => ({
  title: i18n.t(
    'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.steps.levels.wizard_title'
  ),
  nextText: i18n.t('common.next'),
  render: (props) => <RolesStep {...props} />,
  isCompleted: (data) => handleIsCompleted(data),
})

const handleIsCompleted = (data: A3InnuvaWizardData): boolean => {
  const job_positions = a3InnuvaSourceSchema.safeParse(data.sources?.job_positions)
  const mappings = job_positions?.data?.mappings || []

  if (mappings.length === 0) {
    return false
  }
  const parsedData = a3InnuvaSourceSchema.safeParse(job_positions)

  return parsedData.success
}
